import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../cn';

const buttonVariants = cva(
  // Stamped, no transitions, focus-visible stencil shift, label typography uppercase tracked.
  'inline-flex items-center justify-center gap-2 rounded-sm text-[12px] font-medium uppercase tracking-[0.05em] leading-none ' +
    'h-11 px-[18px] select-none whitespace-nowrap ' +
    'focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 ' +
    'disabled:cursor-not-allowed disabled:bg-panel-elev disabled:text-ink-low disabled:hover:bg-panel-elev disabled:hover:text-ink-low',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-strong',
        secondary:
          'bg-transparent text-primary border border-primary hover:bg-primary-soft hover:text-primary-strong',
        ghost:
          'bg-transparent text-ink-mid hover:bg-panel-elev hover:text-ink-high',
        danger:
          'bg-destructive text-destructive-foreground hover:bg-destructive-strong',
      },
      width: {
        auto: '',
        full: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      width: 'auto',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, width, asChild = false, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(buttonVariants({ variant, width }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
